import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Empty } from '@/components/feedback/Empty'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useCubeLiveQuery } from '@/hooks/use-cube-live-query'
import { formatCubeDateTime, formatInteger } from '@/lib/format'
import { cubeWallClock, isTimeOfDay } from '@/lib/hr'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import {
  HAREKET_MEMBERS,
  HR_VIEWS,
  IZIN_MEMBERS,
  hrFilters,
  hrRowsQuery,
  mapHrRows,
  type HareketRow,
  type IzinRow,
} from '@/services/cube/hr'
import { hareketActions, hourFixable } from '@/features/hr/action-specs'
import { useLatestActions, useRunAction } from '@/features/hr/hr-actions'
import { ActionStatusBadge, PersonCell, RecordActions } from '@/features/hr/parts'

/** İzin çakışması iş listesi (İşlerim) ve izin saati düzeltmesi (İ1; İzinler sayfası da kullanır). */
const t = messages.hr
const l = t.leaves
const VIEW = HR_VIEWS.izin

/** İzin saatini düzeltme diyaloğu: gün sabit, yalnız saatler değişir. */
export function HourFixDialog({ leave, onClose }: { leave: IzinRow | null; onClose: () => void }) {
  const start = cubeWallClock(leave?.baslangic)
  const end = cubeWallClock(leave?.bitis)
  const [from, setFrom] = useState(start?.time ?? '')
  const [to, setTo] = useState(end?.time ?? '')
  const run = useRunAction('izin', leave?.id ?? '')
  const valid = isTimeOfDay(from) && isTimeOfDay(to) && from < to
  return (
    <Dialog open={leave !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.hourFix.title}</DialogTitle>
          <DialogDescription>{t.hourFix.body}</DialogDescription>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-3">
          <p className="text-sm">
            {leave?.adisoyadi} · {leave?.izinTuru} · {start?.date}
          </p>
          <div className="flex gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="hr-fix-start">{t.hourFix.start}</Label>
              <Input
                id="hr-fix-start"
                className="w-24 tabular-nums"
                maxLength={5}
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="hr-fix-end">{t.hourFix.end}</Label>
              <Input
                id="hr-fix-end"
                className="w-24 tabular-nums"
                maxLength={5}
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </div>
          </div>
          {!valid && from && to && <p className="text-danger text-xs">{t.hourFix.invalid}</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {t.actions.cancel}
          </Button>
          <Button
            disabled={!valid || !start || run.isPending}
            onClick={() =>
              start &&
              run.mutate(
                {
                  type: 'izin_saat_duzelt',
                  params: { baslangic: `${start.date} ${from}`, bitis: `${start.date} ${to}` },
                },
                { onSuccess: onClose },
              )
            }
          >
            {t.actions.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Faz 3 iş listesi: izin çakışması kategorili, kararsız hareketler + kapsayan izinleri. */
export function ConflictList({ onHourFix }: { onHourFix: (row: IzinRow) => void }) {
  const hView = HR_VIEWS.hareket
  const filters = hrFilters({
    view: hView,
    equals: { hataKategori: ['izin_cakismasi'], durum: ['hatali'] },
  })
  const movements = useCubeLiveQuery({
    queryKey: queryKeys.cube(hView, { kind: 'conflicts' }),
    query: hrRowsQuery({
      view: hView,
      members: HAREKET_MEMBERS,
      filters,
      order: [{ id: 'zaman', desc: true }],
      rowKey: 'id',
      limit: 200,
      offset: 0,
    }),
    select: mapHrRows<HareketRow>(hView, HAREKET_MEMBERS),
    subscribe: false,
  })
  const leaveIds = [
    ...new Set(
      (movements.data ?? []).map((m) => m.kapsayanIzinId).filter((id): id is string => !!id),
    ),
  ]
  const leaves = useCubeLiveQuery({
    queryKey: queryKeys.cube(VIEW, { kind: 'conflict-leaves', leaveIds }),
    query: hrRowsQuery({
      view: VIEW,
      members: IZIN_MEMBERS,
      filters: [{ member: `${VIEW}.id`, operator: 'equals', values: leaveIds }],
      order: [],
      rowKey: 'id',
      limit: 200,
      offset: 0,
    }),
    select: mapHrRows<IzinRow>(VIEW, IZIN_MEMBERS),
    enabled: leaveIds.length > 0,
    subscribe: false,
  })
  const leaveById = new Map((leaves.data ?? []).map((leave) => [leave.id, leave]))
  const actions = useLatestActions(
    'hareket',
    (movements.data ?? []).map((m) => m.id),
    hView,
  )

  if (movements.isLoading) return <Loading rows={4} />
  if (movements.isError)
    return <ErrorState error={movements.error} onRetry={() => void movements.refetch()} />
  const rows = movements.data ?? []
  return (
    <div className="flex flex-col gap-3">
      <p className="text-text-secondary text-sm">{l.conflicts.description}</p>
      {rows.length === 0 ? (
        <Empty hint={l.conflicts.empty} />
      ) : (
        rows.map((movement) => {
          const leave = movement.kapsayanIzinId ? leaveById.get(movement.kapsayanIzinId) : undefined
          return (
            <Card key={movement.id} className="grid gap-4 p-4 md:grid-cols-[1fr_1fr_auto]">
              <section className="flex flex-col gap-2">
                <h3 className="text-text-muted text-xs font-medium">{l.conflicts.movement}</h3>
                <PersonCell
                  name={movement.adisoyadi}
                  sub={`${movement.sicilno ?? ''} · ${movement.sube ?? ''}`}
                />
                <p className="text-sm">
                  {t.girisCikis[movement.tip ?? ''] ?? movement.tip} ·{' '}
                  {formatCubeDateTime(movement.zaman)}
                </p>
                <span className="flex items-center gap-2 text-xs">
                  {t.detail.attempts}: {formatInteger(movement.denemeSayisi)}
                  <ActionStatusBadge action={actions.get(movement.id)} />
                </span>
              </section>
              <section className="flex flex-col gap-2">
                <h3 className="text-text-muted text-xs font-medium">{l.conflicts.leave}</h3>
                {leave ? (
                  <>
                    <p className="text-sm font-medium">{leave.izinTuru}</p>
                    <p className="text-sm">
                      {formatCubeDateTime(leave.baslangic)} – {formatCubeDateTime(leave.bitis)}
                    </p>
                    <span className="flex flex-wrap gap-1.5">
                      <Badge size="sm" tone={leave.saatlik ? 'info' : 'neutral'}>
                        {leave.saatlik ? t.detail.fields.saatlik : t.hourFix.notHourly}
                      </Badge>
                      <Link
                        to={`/ik/izinler?id=${encodeURIComponent(leave.id)}`}
                        className="text-brand text-xs underline-offset-2 hover:underline"
                      >
                        {t.detail.openLeave}
                      </Link>
                    </span>
                  </>
                ) : (
                  <p className="text-text-muted text-sm">{l.conflicts.noLeave}</p>
                )}
              </section>
              <div className="flex flex-col items-start gap-2">
                {leave && hourFixable(leave) && (
                  <RecordActions
                    kind="izin"
                    id={leave.id}
                    actions={[{ type: 'izin_saat_duzelt', onSelect: () => onHourFix(leave) }]}
                  />
                )}
                <RecordActions kind="hareket" id={movement.id} actions={hareketActions(movement)} />
              </div>
            </Card>
          )
        })
      )}
    </div>
  )
}
